import {InlineLink, Text, ThemeProvider, type ColorMode} from '@primer/react-brand'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useEffect} from 'react'
import {clsx} from 'clsx'
import Markdown, {type Components} from 'react-markdown'
import {WebGLPage} from '@github-ui/brand-webgl'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {PageDivider} from './components/PageDivider/PageDivider'
import {Hero} from './components/Hero/Hero'
import {Content} from './components/Content/Content'
import {COLOR_MODE, COPY, FORM_DATA} from './Index.data'

import Styles from './Index.module.css'

const HERO_TRAILING_MARKDOWN_COMPONENTS: Components = {
  a: ({href, children}) => <InlineLink href={href ?? '#'}>{children}</InlineLink>,
  p: ({children}) => (
    <Text as="p" variant="muted" size="100">
      {children}
    </Text>
  ),
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IS_STORYBOOK = (import.meta as any).env?.STORYBOOK

export interface GitHubAppWaitlistPageProps {}

export const GitHubAppWaitlistPage = () => {
  const isFormSubmitted = new URLSearchParams(ssrSafeLocation.search).get('submitted') === 'true'
  const isNewFooter = isFeatureEnabled('site_marketing_footer_react')
  const colorMode: ColorMode = COLOR_MODE ?? 'auto'

  useEffect(() => {
    const wrapper: HTMLElement | null = document?.querySelector(`.${Styles['GitHubAppWaitlistPage']}`)
    if (!wrapper) return

    const updateCanvasOffset = () => {
      if (!wrapper) return

      const top = wrapper.getBoundingClientRect().top + window.scrollY
      wrapper.style.setProperty('--header-height', `${top}px`)
    }

    const resizeObserver = new ResizeObserver(updateCanvasOffset)
    resizeObserver.observe(document.body)
    // eslint-disable-next-line github/no-then
    document.fonts?.ready.then(updateCanvasOffset)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <ThemeProvider
      colorMode={colorMode}
      className={clsx(
        Styles['GitHubAppWaitlistPage'],
        IS_STORYBOOK && Styles['GitHubAppWaitlistPage--storybook'],
        isNewFooter && Styles['GitHubAppWaitlistPage--newFooter'],
      )}
    >
      <WebGLPage {...(colorMode === 'auto' ? {} : {mode: colorMode})}>
        <Hero
          label={COPY.hero.label}
          heading={COPY.hero.headline}
          body={COPY.hero.body}
          cta={COPY.hero.cta}
          trailingComponent={
            COPY.hero.trailingComponent
              ? () => <Markdown components={HERO_TRAILING_MARKDOWN_COMPONENTS}>{COPY.hero.trailingComponent!}</Markdown>
              : undefined
          }
          visual={COPY.hero.visual}
        />

        <div className={Styles['GitHubAppWaitlistPage__spacer']} />

        <Content
          isFormSubmitted={isFormSubmitted}
          content={COPY.content}
          form={{
            copy: COPY.form,
            data: FORM_DATA,
          }}
          thankYou={COPY.thankYou}
        />

        <PageDivider />
      </WebGLPage>
    </ThemeProvider>
  )
}
